import { SiteLayout } from "@/components/layout/SiteLayout";
import { SEO } from "@/components/SEO";
import { Link } from "wouter";
import { breadcrumbSchema, webPageSchema } from "@/lib/seo";

export default function CommunityGuidelines() {
  const page = webPageSchema({
    path: "/community-guidelines",
    title: "Community Guidelines & Content Terms | Shariq Refai, MD",
    description:
      "Community guidelines, comment policy, and user content terms for shariqrefai.com, covering comments, quizzes, forums, and any AI-assisted reader tools.",
    breadcrumbs: [
      { name: "Home", path: "/" },
      { name: "Community Guidelines", path: "/community-guidelines" },
    ],
  });
  return (
    <SiteLayout>
      <SEO
        title="Community Guidelines & Content Terms | Shariq Refai, MD"
        description="Community guidelines, comment policy, and user content terms for shariqrefai.com, covering comments, quizzes, forums, and any AI-assisted reader tools."
        url="/community-guidelines"
        schema={[
          page,
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Community Guidelines", path: "/community-guidelines" },
          ]),
        ]}
      />

      <header className="pt-20 pb-12 px-4 md:px-8 bg-muted/30 border-b border-border/50">
        <div className="container mx-auto max-w-3xl">
          <nav
            aria-label="Breadcrumb"
            className="mb-6 text-xs tracking-widest uppercase text-muted-foreground"
          >
            <Link href="/" className="hover:text-accent transition-colors">
              Home
            </Link>
            <span className="mx-2" aria-hidden="true">/</span>
            <span aria-current="page">Community Guidelines</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-serif text-primary leading-snug">
            Community Guidelines &amp; User Content Terms
          </h1>
          <p className="mt-4 text-lg text-muted-foreground font-light max-w-2xl">
            The standards that apply when readers submit anything to this
            site, today or in the future.
          </p>
        </div>
      </header>

      <article className="py-16 md:py-20 px-4 md:px-8">
        <div className="container mx-auto max-w-3xl prose prose-lg max-w-none prose-headings:font-serif prose-headings:font-normal prose-headings:text-primary prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3 prose-p:font-light prose-p:text-secondary prose-p:leading-relaxed prose-a:text-accent hover:prose-a:text-primary prose-strong:text-primary">
          <p>
            <strong>Last updated: May 23, 2026.</strong>
          </p>

          <h2>Current State</h2>
          <p>
            At present, shariqrefai.com does not host comments, public
            forums, reader profiles, public reviews, or user-facing AI
            tools. The only reader-submitted content channel is the{" "}
            <Link href="/contact">contact form</Link>, which is private and
            covered by the <Link href="/privacy">Privacy Policy</Link> and{" "}
            <Link href="/terms">Terms of Use</Link>.
          </p>
          <p>
            This page sets the standards that will apply if and when
            interactive features are added, including comments, community
            spaces, quizzes, symptom-reflection tools, or AI-assisted
            features. It exists so that readers and contributors know what
            to expect ahead of time.
          </p>

          <h2>Community Guidelines</h2>
          <p>
            Any future comments, discussion threads, or community spaces on
            this site exist for thoughtful, respectful conversation about
            mental health, psychiatry, books, and ideas. Participation is a
            privilege, not a right, and the following are required of all
            contributors:
          </p>
          <ul>
            <li>Be respectful. Disagree with ideas, not people.</li>
            <li>Stay on topic for the page or thread you're contributing to.</li>
            <li>Don't post anything you wouldn't want your name attached to.</li>
            <li>Don't share private information about another person without their consent.</li>
            <li>Don't post content that's discriminatory, harassing, threatening, sexually explicit, defamatory, or that targets any individual or group on the basis of identity.</li>
            <li>Don't promote products, services, supplements, treatments, or schemes.</li>
            <li>Don't impersonate Dr. Refai, any other clinician, or any organization.</li>
            <li>Don't post anything that could be construed as personal medical advice to another reader.</li>
          </ul>

          <h2>Comment Policy</h2>
          <p>
            If comments are ever enabled, they will be moderated. Comments
            may be edited, removed, or refused at editorial discretion when
            they violate these guidelines or the{" "}
            <Link href="/terms">Terms of Use</Link>. Removal is not an
            accusation; it is a moderation decision. Repeated violations
            may result in a permanent ban from commenting.
          </p>

          <h2>Crisis and Safety</h2>
          <p>
            Any reader-facing surface on this site, current or future, is
            not a crisis line. Comments, threads, or AI tools, if added,
            are not monitored in real time for safety concerns and should
            never be used as a substitute for emergency help.
          </p>
          <p>
            If you are in crisis or feel unsafe, call or text{" "}
            <a href="tel:988">988</a> in the United States, call{" "}
            <a href="tel:911">911</a>, or go to the nearest emergency room.
            Outside the United States, contact your local emergency
            services.
          </p>

          <h2>User Content Terms</h2>
          <p>
            "User content" means any text, comment, message, image, audio,
            video, file, response, rating, review, quiz answer, prompt
            input, or other material that you submit to or through this
            website (whether through the contact form, future comments,
            forums, quizzes, or AI tools).
          </p>

          <h3>Your Responsibility</h3>
          <p>
            You're responsible for your user content. You represent that
            you have all rights necessary to submit it; that it doesn't
            infringe anyone else's rights; that it doesn't contain
            unlawful, defamatory, or harassing material; and that you
            understand it is not a clinical communication and is not
            covered by HIPAA on this site.
          </p>

          <h3>License You Grant</h3>
          <p>
            By submitting user content to this website, you grant Shariq
            Refai, MD, MBA, and his affiliated organizations a worldwide,
            non-exclusive, royalty-free, transferable, sublicensable
            license to host, store, reproduce, adapt (for example, for
            length or clarity), display, and use that content in
            connection with operating, improving, and promoting this site
            and its educational content. You retain ownership of your
            content.
          </p>

          <h3>No Confidential Relationship</h3>
          <p>
            Submitting user content does not create a physician-patient,
            therapist-client, treatment, fiduciary, or confidential
            medical relationship of any kind. Do not submit information
            you intend to be confidential medical communication. See the{" "}
            <Link href="/disclaimer">Medical &amp; Educational
            Disclaimer</Link> for the full position.
          </p>

          <h3>Removal and Retention</h3>
          <p>
            User content may be edited or removed at editorial discretion
            at any time, with or without notice, where it violates these
            terms or the{" "}
            <Link href="/terms">Terms of Use</Link>. Retention of contact-form
            and other submissions is described in the{" "}
            <Link href="/privacy">Privacy Policy</Link>.
          </p>

          <h2>Quizzes, Symptom Tools, and Reflection Features</h2>
          <p>
            If quizzes, symptom-reflection tools, calculators, or
            self-assessment features are added in the future, they are
            informational reflection prompts, not diagnostic instruments.
            Specifically:
          </p>
          <ul>
            <li>They will not say "you have" a condition.</li>
            <li>They will not say "you likely have" a condition.</li>
            <li>They will use neutral language such as "your responses may reflect patterns sometimes associated with..." and direct readers to a qualified clinician for any actual evaluation.</li>
            <li>They will state, prominently, that results are informational only and are not a diagnosis.</li>
          </ul>

          <h2>AI-Assisted Reader Tools</h2>
          <p>
            If AI-assisted features are ever added to this site for
            readers (for example, a search assistant, a glossary
            explainer, or a content summarizer), they will:
          </p>
          <ul>
            <li>Be labeled clearly as AI-assisted.</li>
            <li>Not provide diagnosis, treatment, dosing, or individualized medical advice.</li>
            <li>Not act as a chat therapist, crisis line, or monitoring service.</li>
            <li>Not retain or train on identifying reader inputs without explicit, separate consent.</li>
          </ul>
          <p>
            See the <Link href="/ai-policy">AI Use &amp; Editorial
            Assistance Policy</Link> for the broader position on AI.
          </p>

          <h2>Reporting a Violation</h2>
          <p>
            If you see content on this site that violates these guidelines
            (in the future, in a comment, thread, or tool), or content
            that infringes a copyright you own, please report it. For
            copyright issues, see the{" "}
            <Link href="/copyright">Copyright</Link> page. For everything
            else, email{" "}
            <a href="mailto:hello@shariqrefai.com">hello@shariqrefai.com</a>{" "}
            or use the <Link href="/contact">contact page</Link>.
          </p>

          <h2>Changes</h2>
          <p>
            These guidelines may be updated as features are added or as
            standards evolve. The updated version will be posted here with
            a revised "Last updated" date.
          </p>
        </div>
      </article>
    </SiteLayout>
  );
}
